import React, { useState, useEffect } from 'react';
import { gql, useMutation } from '@apollo/client';
import Grid from '@mui/material/Grid';
import Box from '@mui/material/Box';
import Paper from '@mui/material/Paper';
import TextField from '@mui/material/TextField';
import MenuItem from '@mui/material/MenuItem';
import Container from '@mui/material/Container';
import Button from '@mui/material/Button';
import Alert from '@mui/material/Alert';
import AlertTitle from '@mui/material/AlertTitle';
import Backdrop from '@mui/material/Backdrop';
import CircularProgress from '@mui/material/CircularProgress';

import { Save as SaveIcon } from 'lucide-react';

import ImageDrop from '../common/ImageDrop';
import ImageList from '../common/ImageList';
import { clientStatus } from '../../utils/selectOptions';

const SET_COMPANY = gql`
    mutation ModifyClient($rid: String, $name: String, $website: String, $phoneNumber: String, $address: String, $addressTwo: String, $city: String, $stateRegion: String, $postalCode: String, $country: String, $crmId: String, $status: String, $imgFile: Upload) {
        modifyClient(rid: $rid, name: $name, website: $website, phoneNumber: $phoneNumber, address: $address, addressTwo: $addressTwo, city: $city, stateRegion: $stateRegion, postalCode: $postalCode, country: $country, crmId: $crmId, status: $status, imgFile: $imgFile){
            responseCode
            responseText
            actionType
            objectType
            objectId
		}
	}
`;

const scrollToTop = () =>{window.scrollTo({top: 0, behavior: 'smooth'});};

export default function ModifyForm(props) {
    const { data, navGoBack, navToUpdate, refetchQuery, b2Auth, businessRid } = props;
    const [isLoading, setIsLoading] = useState(false);
    const [serverErr, setServerErr] = useState(false);

    const data_rid = data?.clientdetail[0].result.rid;
    const data_name = data?.clientdetail[0].result.name;
    const data_website = data?.clientdetail[0].result.website;
    const data_phoneNumber = data?.clientdetail[0].result.phoneNumber;
    const data_address = data?.clientdetail[0].result.address;
    const data_addressTwo = data?.clientdetail[0].result.addressTwo;
    const data_city = data?.clientdetail[0].result.city;
    const data_stateRegion = data?.clientdetail[0].result.stateRegion;
    const data_postalCode = data?.clientdetail[0].result.postalCode;
    const data_country = data?.clientdetail[0].result.country;
    const data_crmId = data?.clientdetail[0].result.crmId;
    const data_status = data?.clientdetail[0].result.status;
    const foundImages = data?.clientdetail[0].result?.images;

    const [displayImages, setDisplayImages] = useState(null);
    const [imgFile, setImgFile] = useState(false);

    useEffect(() => {
        if(foundImages && foundImages !== "[]"){
            const displayImagesPreSort = JSON.parse(foundImages);
            setDisplayImages(displayImagesPreSort.sort((a, b)=>{return a.displayOrder - b.displayOrder;}));
        }
    }, [foundImages]);

    const [errCode, errCodeSet] = useState(false);
    const [nameErr, nameErrSet] = useState(false);
    const [statusErr, statusErrSet] = useState(false);

    const [status, statusSet] = useState(data_status || '');
    const handleChangestatus = (event) => {
        statusSet(event.target.value);
    };

    const [name, nameSet] = useState(data_name || '');
    const handleChangename = (event) => {
        nameSet(event.target.value);
    };

    const [website, websiteSet] = useState(data_website || '');
    const handleChangewebsite = (event) => {
        websiteSet(event.target.value);
    };

    const [phoneNumber, phoneNumberSet] = useState(data_phoneNumber || '');
    const handleChangephoneNumber = (event) => {
        phoneNumberSet(event.target.value);
    };

    const [address, addressSet] = useState(data_address || '');
    const handleChangeaddress = (event) => {
        addressSet(event.target.value);
    };

    const [addressTwo, addressTwoSet] = useState(data_addressTwo || '');
    const handleChangeaddressTwo = (event) => {
        addressTwoSet(event.target.value);
    };

    const [city, citySet] = useState(data_city || '');
    const handleChangecity = (event) => {
        citySet(event.target.value);
    };

    const [stateRegion, stateRegionSet] = useState(data_stateRegion || '');
    const handleChangestateRegion = (event) => {
        stateRegionSet(event.target.value);
    };

    const [postalCode, postalCodeSet] = useState(data_postalCode || '');
    const handleChangepostalCode = (event) => {
        postalCodeSet(event.target.value);
    };

    const [country, countrySet] = useState(data_country || '');
    const handleChangecountry = (event) => {
        countrySet(event.target.value);
    };

    const [crmId, crmIdSet] = useState(data_crmId || '');
    const handleChangecrmId = (event) => {
        crmIdSet(event.target.value);
    };

    const collectFormData = () => {
        const formData = {};
        let formValid = true;

        errCodeSet(false);
        setServerErr(false);
        nameErrSet(false);
        statusErrSet(false);

        if(!data_rid || data_rid === ""){
            formValid = false;
        }

        if (!name || name === ""){
            nameErrSet(true);
            formValid = false;
        }

        if (!status || status === ""){
            statusErrSet(true);
            formValid = false;
        }

        if (formValid){
            setServerErr(false);
            formData.rid = data_rid;
            formData.name = name;
            formData.website = website;
            formData.phoneNumber = phoneNumber;
            formData.address = address;
            formData.addressTwo = addressTwo;
            formData.city = city;
            formData.stateRegion = stateRegion;
            formData.postalCode = postalCode;
            formData.country = country;
            formData.crmId = crmId;
            formData.status = status;
            formData.imgFile = imgFile[0];

            setIsLoading(true);
            setCompany({variables:{...formData}});
        }
        else{
            scrollToTop();
            if(!errCode){
                errCodeSet(true);
            }
        }
        
    }

    const [setCompany] = useMutation(SET_COMPANY, {
        onCompleted: (setData) => {
            setIsLoading(false);
            if (setData && setData?.modifyClient?.responseCode === "200"){
                const message = setData?.modifyClient?.responseText;
                navToUpdate(data_rid,message);
            }
            else{
                setServerErr(`There was an issue with your request.`);
                scrollToTop();
            }
        },
        onError: (setError) => {
            setIsLoading(false);
            setServerErr(`There was an error processing your request.`);
            scrollToTop();
        }
    });

    return (
        <Container maxWidth="sm">            
            {isLoading && 
                <Backdrop sx={{ color: '#fff', zIndex: (theme) => theme.zIndex.drawer + 1 }} open={true}><CircularProgress sx={{color:'#c9fb00'}} thickness={1} size={60} /></Backdrop>
            }

            {serverErr &&
            <Grid container direction="row" justifyContent="center" alignItems="center" sx={{ marginTop: '12px', marginBottom: '12px'}}>
                <Grid item>
                    <Alert severity="error" variant="outlined">
                        <AlertTitle>Error</AlertTitle>
                        {serverErr}
                    </Alert>
                </Grid>
            </Grid>
            }

            {errCode &&
            <Grid container direction="row" justifyContent="center" alignItems="center" sx={{ marginTop: '12px', marginBottom: '12px'}}>
                <Grid item>
                    <Alert severity="error" variant="outlined">
                        <AlertTitle>Error</AlertTitle>
                        Please resolve the form errors below!
                    </Alert>
                </Grid>
            </Grid>
            }

            <Paper sx={{ width: '100%', padding: '20px', marginBottom: '3rem' }}>
                <Box component="form" sx={{ '& > :not(style)': { padding: '20px', paddingBottom: '30px' } }}>

                    <TextField variant="standard" id="name" label="Name" error={nameErr} value={name} onChange={handleChangename} fullWidth />

                    <TextField variant="standard" id="website" label="Website" value={website} onChange={handleChangewebsite} fullWidth />

                    <TextField variant="standard" id="crmId" label="CRM ID" value={crmId} onChange={handleChangecrmId} fullWidth />

                    <TextField variant="standard" id="status" label="Status" error={statusErr} value={status} onChange={handleChangestatus} select fullWidth>
                        {clientStatus.map((option) => (<MenuItem key={option.value} value={option.value}>{option.label}</MenuItem>))}
                    </TextField>

                    <TextField variant="standard" id="phoneNumber" label="Phone Number" value={phoneNumber} onChange={handleChangephoneNumber} fullWidth />
                    <TextField variant="standard" id="address" label="Address" value={address} onChange={handleChangeaddress} fullWidth />
                    <TextField variant="standard" id="addressTwo" label="Address Two" value={addressTwo} onChange={handleChangeaddressTwo} fullWidth />
                    <TextField variant="standard" id="city" label="City" value={city} onChange={handleChangecity} fullWidth />
                    <TextField variant="standard" id="stateRegion" label="State / Region" value={stateRegion} onChange={handleChangestateRegion} fullWidth />
                    <TextField variant="standard" id="postalCode" label="Postal Code" value={postalCode} onChange={handleChangepostalCode} fullWidth />
                    <TextField variant="standard" id="country" label="Country" value={country} onChange={handleChangecountry} fullWidth />
                
                </Box>

                {/* <Paper sx={{ width: '100%', padding: '20px', marginBottom: '20px' }}> */}
                <Box sx={{paddingTop:'20px',paddingBottom:'20px'}}>
                    <ImageList imgType="client" sourceTypeId={data_rid} images={displayImages} refetchQuery={refetchQuery} b2Auth={b2Auth} businessRid={businessRid} />
                    <ImageDrop setImgFile={setImgFile} />
                </Box>
                {/* </Paper> */}

                <Grid container direction="row" justifyContent="space-between" alignItems="center" sx={{marginTop: '20px'}}>
                    <Grid item>
                        <Button aria-label="cancel" variant="outlined" onClick={navGoBack}>Cancel</Button>
                    </Grid>
                    <Grid item>
                        <Button aria-label="save form" variant="outlined" onClick={collectFormData} endIcon={<SaveIcon strokeWidth={1} size={16} />}>Save Company</Button>
                    </Grid>
                </Grid>

            </Paper>

        </Container>
    );
}